'use client';

import Link from 'next/link';

import { ArrowRight } from 'lucide-react';

import type { MarketingLocale } from '~/lib/marketing/site';
import { localizedHref } from '~/lib/marketing/routes';

import { HOME_HERO } from '../../_lib/platform.content';
import { TRUST_BAR } from '../../_lib/home.content';
import { SOLUTIONS } from '../../_lib/solutions.content';
import { HeroDemo } from '../product-demos/hero-demo';
import { SOLUTION_ICONS } from '../solutions-menu';

export function HeroSection({
  locale,
  onScheduleDemo,
}: {
  locale: MarketingLocale;
  onScheduleDemo: () => void;
}) {
  return (
    <section id="home" className="relative overflow-hidden bg-[#202020] text-white">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -top-48 end-[-12%] h-[760px] w-[760px] rounded-full bg-[#a9c8ff]/[0.09] blur-[140px]" />
        <div className="absolute bottom-[-30%] start-[-10%] h-[520px] w-[520px] rounded-full bg-[#ede8e2]/[0.05] blur-[120px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:radial-gradient(ellipse_70%_60%_at_60%_40%,black,transparent)]" />
      </div>

      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-12 px-4 pb-14 pt-[118px] md:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-10 lg:px-[90px] lg:pb-20 lg:pt-[140px]">
        <div className="z-10">
          <div className="flow-rise mb-6 flex items-center gap-3" style={{ animationDelay: '0s' }}>
            <span className="h-2 w-2 animate-pulse rounded-full bg-[#a9c8ff]" aria-hidden="true" />
            <p className="font-roboto-mono text-[12px] uppercase tracking-wide text-[#a9c8ff] md:text-[13px]">
              {HOME_HERO.label[locale]}
            </p>
          </div>

          {/* Static on purpose: the headline is the largest element above the
              fold, so it must paint from the server HTML, not after hydration. */}
          <h1
            className="font-roboto text-[38px] leading-[1.04] tracking-tight md:text-[56px] lg:text-[68px]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            <span className="block">{HOME_HERO.title[locale]}</span>
            <span className="mt-2 block text-white/45">{HOME_HERO.titleMuted[locale]}</span>
          </h1>

          <p
            className="flow-rise mt-7 max-w-[540px] font-roboto text-[16px] leading-relaxed text-white/65 md:text-[18px]"
            style={{ animationDelay: '0.28s' }}
          >
            {HOME_HERO.description[locale]}
          </p>

          <div className="flow-rise mt-9 flex flex-wrap items-center gap-4" style={{ animationDelay: '0.36s' }}>
            <button
              type="button"
              onClick={onScheduleDemo}
              className="group inline-flex h-[52px] cursor-pointer items-center gap-3 bg-white px-7 text-black transition-colors hover:bg-[#ede8e2] md:h-[56px] md:px-8"
            >
              <span className="font-roboto-mono text-[12px] font-bold uppercase md:text-[14px]">
                {HOME_HERO.primaryCta[locale]}
              </span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
            </button>
            <Link
              href="#platform"
              className="inline-flex h-[52px] items-center border border-white/15 px-7 transition-colors hover:bg-white/5 md:h-[56px] md:px-8"
            >
              <span className="font-roboto-mono text-[12px] uppercase md:text-[14px]">
                {HOME_HERO.secondaryCta[locale]}
              </span>
            </Link>
          </div>

          <div className="flow-rise mt-10" style={{ animationDelay: '0.46s' }}>
            <p className="mb-3 font-roboto-mono text-[11px] uppercase tracking-wide text-white/40">
              {HOME_HERO.audienceLead[locale]}
            </p>
            <ul className="flex flex-wrap gap-2">
              {SOLUTIONS.map((solution) => {
                const Icon = SOLUTION_ICONS[solution.slug];

                return (
                  <li key={solution.slug}>
                    <Link
                      href={localizedHref(solution.href, locale)}
                      className="inline-flex min-h-11 items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-4 font-roboto text-[13px] text-white/85 transition-colors hover:border-white/30 hover:bg-white/[0.08] hover:text-white"
                    >
                      <Icon className="h-3.5 w-3.5 text-[#a9c8ff]" />
                      {solution.tabLabel[locale]}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="relative mx-auto w-full max-w-[660px]">
          <HeroDemo locale={locale} />
        </div>
      </div>

      <div className="relative border-t border-white/10">
        <dl className="mx-auto grid max-w-[1440px] grid-cols-2 gap-6 px-4 py-8 md:grid-cols-4 md:px-8 lg:px-[90px]">
          {TRUST_BAR.stats.map((stat) => (
            <div key={stat.label.en} className="flex flex-col-reverse">
              <dt className="mt-1 font-roboto-mono text-[11px] uppercase text-white/40 md:text-[12px]">
                {stat.label[locale]}
              </dt>
              <dd className="font-roboto text-[26px] font-bold md:text-[32px]">{stat.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
